import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function Activities() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState('')
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)

  const handleBooking = (activity) => {
    setSelectedActivity(activity)
    setIsModalOpen(true)
  }

  // Hardcoded activities as fallback
  const hardcodedActivities = [
    // PRIMATE ENCOUNTERS
    {
      title: 'Gorilla Trekking',
      icon: 'fa-paw',
      tagline: 'Once-in-a-Lifetime Encounter',
      description: 'Trek through the misty forests of Bwindi or Mgahinga to meet mountain gorillas in their natural habitat. Navigate steep, forested terrain to spend one precious hour observing these gentle giants as they play, feed, and interact. The moment you lock eyes with a silverback is worth every challenging step.',
      image: 'https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800',
      duration: '2-8 hours',
      difficulty: 'Very Strenuous',
      price: 'From $800 per person',
      destination: 'Bwindi Impenetrable',
      category: 'Primate Encounters',
      link: '/activities/gorilla-trekking'
    },
    {
      title: 'Gorilla Habituation Experience',
      icon: 'fa-clock',
      tagline: 'Extended Time with Gorillas',
      description: 'Join researchers for a full day with semi-habituated gorilla groups in Bwindi. Enjoy four hours of close observation while contributing to conservation science. This exclusive experience offers deeper insights into gorilla behavior and family dynamics.',
      image: 'https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800',
      duration: '4 hours observation',
      difficulty: 'Very Strenuous',
      price: 'From $1,500 per person',
      destination: 'Bwindi Impenetrable',
      category: 'Primate Encounters',
      link: '/activities/gorilla-habituation'
    },
    {
      title: 'Chimpanzee Trekking',
      icon: 'fa-paw',
      tagline: 'Our Closest Relatives',
      description: 'Follow expert trackers through Kibale Forest to locate habituated chimpanzee groups. Watch as these intelligent primates swing through the canopy, groom each other, and communicate with distinctive pant-hooting calls. The forest comes alive with their playful antics and complex social interactions.',
      image: 'https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800',
      duration: '2-5 hours',
      difficulty: 'Moderate',
      price: 'From $200 per person',
      destination: 'Kibale National Park',
      category: 'Primate Encounters',
      link: '/activities/chimpanzee-trekking'
    },
    {
      title: 'Chimpanzee Habituation',
      icon: 'fa-clock',
      tagline: 'Full Day with Chimps',
      description: 'Spend an entire day following chimps from their dawn nest building to dusk feeding in Kibale Forest. This immersive experience lets you observe natural behaviors rarely seen on standard treks, including hunting, tool use, and territorial displays.',
      image: 'https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800',
      duration: 'Full day',
      difficulty: 'Moderate to Strenuous',
      price: 'From $250 per person',
      destination: 'Kibale National Park',
      category: 'Primate Encounters',
      link: '/activities/chimp-habituation'
    },
    {
      title: 'Golden Monkey Tracking',
      icon: 'fa-paw',
      tagline: 'Bamboo Forest Primates',
      description: 'Track the rare golden monkeys through bamboo forests in Mgahinga Gorilla National Park. These beautiful primates with golden-orange fur are playful and acrobatic, leaping through bamboo with incredible agility. Easier than gorilla trekking but equally rewarding.',
      image: 'https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800',
      duration: '3-4 hours',
      difficulty: 'Moderate',
      price: 'From $100 per person',
      destination: 'Mgahinga Gorilla NP',
      category: 'Primate Encounters',
      link: '/activities/golden-monkey-tracking'
    },

    // MOUNTAIN ADVENTURES
    {
      title: 'Rwenzori Mountains Trek',
      icon: 'fa-mountain',
      tagline: 'Mountains of the Moon',
      description: 'Challenge yourself on Uganda\'s highest peaks in the legendary Rwenzori Mountains. Multi-day treks take you through changing ecosystems from montane forest to alpine zones and glaciers. Summit Margherita Peak at 5,109m or choose shorter routes to stunning crater lakes.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '7-10 days',
      difficulty: 'Very Strenuous',
      price: 'From $1,200 per person',
      destination: 'Rwenzori Mountains NP',
      category: 'Mountain Adventures',
      link: '/experiences/rwenzori-mountains-trek'
    },
    {
      title: 'Mount Elgon Trekking',
      icon: 'fa-mountain',
      tagline: 'Giant Caldera Exploration',
      description: 'Trek to Wagagai Peak (4,321m) on Mount Elgon, exploring one of Africa\'s largest volcanic calderas. Discover lava tube caves, stunning waterfalls, and unique Afro-alpine vegetation. Multi-day routes range from moderate to challenging with spectacular scenery throughout.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '4-6 days',
      difficulty: 'Very Strenuous',
      price: 'From $600 per person',
      destination: 'Mount Elgon NP',
      category: 'Mountain Adventures',
      link: '/activities/mount-elgon-trekking'
    },
    {
      title: 'Virunga Volcano Climbing',
      icon: 'fa-mountain',
      tagline: 'Summit Volcanic Peaks',
      description: 'Climb the dramatic Virunga volcanoes in Mgahinga. Choose Mount Gahinga (3-6 hours), Mount Sabinyo with ladder sections (8 hours), or challenging Mount Muhabura (8-10 hours). Stand where Uganda, Rwanda, and Congo meet with panoramic mountain views.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '3-10 hours',
      difficulty: 'Strenuous to Very Strenuous',
      price: 'From $80 per person',
      destination: 'Mgahinga Gorilla NP',
      category: 'Mountain Adventures',
      link: '/activities/virunga-volcano-climbing'
    },
    {
      title: 'Hot Air Balloon Safari',
      icon: 'fa-hot-air-balloon',
      tagline: 'Float Above the Savanna',
      description: 'Soar above Queen Elizabeth National Park at sunrise in a hot air balloon. Drift silently over the plains watching elephants, buffalo, and antelope from a unique aerial perspective. Celebrate with a champagne bush breakfast after landing.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '3 hours',
      difficulty: 'Easy',
      price: 'From $380 per person',
      destination: 'Queen Elizabeth NP',
      category: 'Mountain Adventures',
      link: '/activities/hot-air-balloon-safari'
    },
    {
      title: 'Sipi Falls Abseiling',
      icon: 'fa-person-falling',
      tagline: 'Rappel Down Waterfalls',
      description: 'Experience the thrill of abseiling down the 100-meter Sipi Falls on Mount Elgon. Professional guides ensure safety as you descend alongside cascading water with breathtaking valley views. Technical equipment provided for this unforgettable adrenaline rush.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '3-4 hours',
      difficulty: 'Strenuous',
      price: 'From $100 per person',
      destination: 'Mount Elgon',
      category: 'Mountain Adventures',
      link: '/activities/sipi-falls-abseiling'
    },

    // WATER & RIVER SPORTS
    {
      title: 'Whitewater Rafting',
      icon: 'fa-water',
      tagline: 'Conquer the Nile Rapids',
      description: 'Challenge the legendary rapids of the Nile River at Jinja. Navigate grade 3-5 rapids with names like "The Bad Place" and "Overtime." Professional guides and safety kayakers ensure thrilling yet safe runs. Half-day or full-day adventures available for all experience levels.',
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800',
      duration: 'Half or full day',
      difficulty: 'Strenuous',
      price: 'From $110 per person',
      destination: 'Jinja',
      category: 'Water & River Sports',
      link: '/activities/whitewater-rafting'
    },
    {
      title: 'Kayaking Adventures',
      icon: 'fa-person-rowing',
      tagline: 'Paddle the Mighty Nile',
      description: 'Explore the Nile by kayak with options for both whitewater and flatwater paddling. Learn from expert instructors or challenge yourself on exciting rapids. Multi-day kayak safaris available for experienced paddlers seeking the ultimate river adventure.',
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800',
      duration: '2-8 hours',
      difficulty: 'Moderate to Strenuous',
      price: 'From $80 per person',
      destination: 'Jinja',
      category: 'Water & River Sports',
      link: '/activities/kayaking-adventures'
    },
    {
      title: 'Lake Bunyonyi Canoeing',
      icon: 'fa-person-rowing',
      tagline: 'Peaceful Island Exploration',
      description: 'Paddle across bilharzia-free Lake Bunyonyi, exploring 29 islands at your own pace. Visit Punishment Island, learn local history, and enjoy swimming in safe waters. Perfect for families and those seeking tranquil water experiences with stunning scenery.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
      duration: '2-4 hours',
      difficulty: 'Easy',
      price: 'From $30 per person',
      destination: 'Lake Bunyonyi',
      category: 'Water & River Sports',
      link: '/activities/lake-bunyonyi-canoeing'
    },
    {
      title: 'Boat Safaris',
      icon: 'fa-ship',
      tagline: 'Water-Based Wildlife Viewing',
      description: 'Glide along the Kazinga Channel, Nile River, or Lake Mburo for unique wildlife perspectives. See massive hippo pods, Nile crocodiles, elephants drinking, and incredible water birds. Sunset cruises offer magical golden hour photography opportunities.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
      duration: '2-3 hours',
      difficulty: 'Easy',
      price: 'From $30 per person',
      destination: 'Multiple Parks',
      category: 'Water & River Sports',
      link: '/activities/boat-safaris'
    },

    // WILDLIFE SAFARIS
    {
      title: 'Game Drives',
      icon: 'fa-jeep',
      tagline: 'Classic Safari Adventure',
      description: 'Experience the thrill of tracking wildlife across Uganda\'s diverse landscapes in custom-equipped safari vehicles. Expert guides help you spot lions, elephants, buffaloes, and leopards while sharing fascinating insights. Early morning and sunset drives offer the best wildlife viewing.',
      image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800',
      duration: 'Half or full day',
      difficulty: 'Easy',
      price: 'From $50 per person',
      destination: 'Multiple Parks',
      category: 'Wildlife Safaris',
      link: '/activities/queen-elizabeth-game-drives'
    },
    {
      title: 'Lion Tracking Experience',
      icon: 'fa-paw',
      tagline: 'Research-Based Wildlife Encounter',
      description: 'Join researchers using radio telemetry to track lions in Queen Elizabeth National Park. Learn about conservation efforts while locating pride movements. This unique experience combines wildlife viewing with scientific research participation and education.',
      image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800',
      duration: '3-4 hours',
      difficulty: 'Moderate',
      price: 'From $60 per person',
      destination: 'Queen Elizabeth NP',
      category: 'Wildlife Safaris',
      link: '/activities/lion-tracking'
    },
    {
      title: 'Walking Safaris',
      icon: 'fa-person-hiking',
      tagline: 'Wildlife on Foot',
      description: 'Experience intimate wildlife encounters on guided walking safaris in Lake Mburo and Kidepo Valley. Without dangerous predators in some areas, you can safely walk among zebras, giraffes, and antelopes. Expert rangers share tracking skills and wilderness knowledge.',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800',
      duration: '2-4 hours',
      difficulty: 'Moderate',
      price: 'From $30 per person',
      destination: 'Lake Mburo NP',
      category: 'Wildlife Safaris'
    },
    {
      title: 'Horseback Safaris',
      icon: 'fa-horse',
      tagline: 'Safari on Horseback',
      description: 'Explore Lake Mburo National Park from horseback for a unique wildlife viewing experience. Ride alongside zebras and impalas as animals perceive you as another grazing creature. No previous riding experience necessary for gentle rides.',
      image: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800',
      duration: '1-4 hours',
      difficulty: 'Easy to Moderate',
      price: 'From $80 per person',
      destination: 'Lake Mburo NP',
      category: 'Wildlife Safaris',
      link: '/activities/horseback-riding-jinja'
    },
    {
      title: 'Night Game Drives',
      icon: 'fa-moon',
      tagline: 'After-Dark Wildlife',
      description: 'Discover nocturnal wildlife on specialized night drives. Using spotlights, spot leopards hunting, hyenas on the move, bushbabies, genets, and owls. Experience the African bush\'s atmospheric night sounds and see animals never visible during daylight hours.',
      image: 'https://images.unsplash.com/photo-1549366021-9f761d450615?w=800',
      duration: '2-3 hours',
      difficulty: 'Easy',
      price: 'From $40 per person',
      destination: 'Multiple Parks',
      category: 'Wildlife Safaris'
    },

    // CULTURAL EXPERIENCES
    {
      title: 'Batwa Cultural Trail',
      icon: 'fa-masks-theater',
      tagline: 'Forest People Heritage',
      description: 'Connect with the Batwa pygmies, the original forest inhabitants of Bwindi and Mgahinga. Learn traditional hunting techniques, medicinal plant uses, fire-making, and forest survival skills. Visit sacred caves and hear ancient stories of forest life.',
      image: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800',
      duration: '3-4 hours',
      difficulty: 'Moderate',
      price: 'From $30 per person',
      destination: 'Bwindi/Mgahinga',
      category: 'Cultural Experiences',
      link: '/activities/batwa-trail'
    },
    {
      title: 'Karamojong Village Encounters',
      icon: 'fa-people-group',
      tagline: 'Traditional Pastoralist Culture',
      description: 'Visit Karamojong communities in remote Kidepo Valley to experience one of Africa\'s last traditional pastoralist cultures. Witness daily life, cattle ceremonies, traditional dances, and learn about customs that have endured for centuries.',
      image: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800',
      duration: '3-5 hours',
      difficulty: 'Moderate',
      price: 'From $30 per person',
      destination: 'Kidepo Valley NP',
      category: 'Cultural Experiences'
    },
    {
      title: 'Ik Mountain Community Visit',
      icon: 'fa-people-group',
      tagline: 'Remote Mountain Tribe',
      description: 'Trek to meet the Ik people who live high in the Morungole Mountains of Kidepo. This challenging but rewarding journey introduces you to one of Uganda\'s most isolated communities, with unique language and traditions shaped by their mountainous environment.',
      image: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800',
      duration: 'Full day',
      difficulty: 'Strenuous',
      price: 'From $50 per person',
      destination: 'Kidepo Valley NP',
      category: 'Cultural Experiences'
    },
    {
      title: 'Community Village Walks',
      icon: 'fa-people-group',
      tagline: 'Authentic Local Life',
      description: 'Walk through rural villages surrounding national parks to experience everyday Ugandan life. Visit schools, community projects, local markets, and homes. Learn traditional farming, cooking, and crafts while supporting communities through responsible tourism.',
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800',
      duration: '2-4 hours',
      difficulty: 'Easy',
      price: 'From $20 per person',
      destination: 'Multiple Locations',
      category: 'Cultural Experiences'
    },

    // EXTREME ADVENTURES
    {
      title: 'Bungee Jumping',
      icon: 'fa-person-falling-burst',
      tagline: 'Leap Over the Nile',
      description: 'Take a 44-meter leap of faith over the mighty Nile River at Jinja. This iconic bungee jump offers spectacular views and an incredible adrenaline rush. Professional operators ensure world-class safety standards while you experience Africa\'s ultimate thrill.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '2-3 hours',
      difficulty: 'Moderate (requires courage!)',
      price: 'From $115 per person',
      destination: 'Jinja',
      category: 'Extreme Adventures',
      link: '/activities/bungee-jumping'
    },
    {
      title: 'Zip-Lining',
      icon: 'fa-person-falling',
      tagline: 'Fly Over Landscapes',
      description: 'Soar through the air on thrilling zip-line courses. Fly over Lake Bunyonyi\'s islands or through forest canopy on various routes. Safe, exhilarating, and offering unique aerial perspectives of Uganda\'s stunning landscapes.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '2-3 hours',
      difficulty: 'Moderate',
      price: 'From $60 per person',
      destination: 'Lake Bunyonyi/Multiple',
      category: 'Extreme Adventures'
    },
    {
      title: 'Quad Biking',
      icon: 'fa-motorcycle',
      tagline: 'Off-Road River Adventures',
      description: 'Explore rugged trails along the Nile on all-terrain quad bikes. Navigate riverside paths, local villages, and challenging terrain with powerful machines. Both guided tours and solo rides available for licensed riders seeking off-road excitement.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '1-3 hours',
      difficulty: 'Easy to Moderate',
      price: 'From $80 per person',
      destination: 'Jinja',
      category: 'Extreme Adventures',
      link: '/activities/quad-biking'
    },
    {
      title: 'Mountain Biking',
      icon: 'fa-person-biking',
      tagline: 'Pedal Through Wilderness',
      description: 'Cycle through Lake Mburo National Park or along Nile River trails on guided mountain bike safaris. Traverse varying terrain from savanna to riverside paths while spotting wildlife. Bikes and safety gear provided for this active adventure.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '2-4 hours',
      difficulty: 'Moderate',
      price: 'From $50 per person',
      destination: 'Lake Mburo/Jinja',
      category: 'Extreme Adventures',
      link: '/activities/mountain-biking-jinja'
    },

    // NATURE EXPLORATION
    {
      title: 'Bird Watching Expeditions',
      icon: 'fa-dove',
      tagline: 'Over 1,000 Species',
      description: 'Uganda is a birder\'s paradise with over 1,000 recorded species including rare Shoebill Storks, Albertine Rift endemics, and Great Blue Turacos. Specialized guides help identify birds by sight and sound across diverse habitats from swamps to montane forests.',
      image: 'https://images.unsplash.com/photo-1582967788606-a171c1080cb0?w=800',
      duration: 'Half to full day',
      difficulty: 'Easy to Moderate',
      price: 'From $45 per person',
      destination: 'Multiple Parks',
      category: 'Nature Exploration',
      link: '/activities/bwindi-birdwatching'
    },
    {
      title: 'Forest Nature Walks',
      icon: 'fa-person-hiking',
      tagline: 'Discover Hidden Trails',
      description: 'Explore diverse forest ecosystems on guided nature walks. From waterfall trails in Bwindi to swamp boardwalks in Kibale\'s Bigodi Wetland. Notice smaller wonders - butterflies, orchids, medicinal plants, and fascinating insects missed on vehicle safaris.',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800',
      duration: '2-5 hours',
      difficulty: 'Easy to Moderate',
      price: 'From $30 per person',
      destination: 'Multiple Parks',
      category: 'Nature Exploration',
      link: '/activities/bwindi-nature-walks'
    },
    {
      title: 'Cave Exploration',
      icon: 'fa-mountain',
      tagline: 'Underground Adventures',
      description: 'Explore dramatic lava tube caves on Mount Elgon or Batwa cultural caves in Mgahinga. Discover underground rivers, bat colonies, and unique geological formations. Some caves feature ancient cultural significance and rock paintings.',
      image: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=800',
      duration: '2-4 hours',
      difficulty: 'Moderate',
      price: 'From $40 per person',
      destination: 'Mount Elgon/Mgahinga',
      category: 'Nature Exploration'
    },
    {
      title: 'Hot Springs Hikes',
      icon: 'fa-fire',
      tagline: 'Geothermal Wonders',
      description: 'Hike to Sempaya Hot Springs in Semuliki Valley where boiling geothermal springs emerge dramatically from the ground. Watch locals cook eggs and plantains in the near-100°C water. Learn about the geological forces creating these natural phenomena.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '3-4 hours',
      difficulty: 'Moderate',
      price: 'From $50 per person',
      destination: 'Semuliki Valley NP',
      category: 'Nature Exploration'
    },
    {
      title: 'Crater Lake Hikes',
      icon: 'fa-water',
      tagline: 'Volcanic Legacy Trails',
      description: 'Trek around stunning crater lakes formed by ancient volcanic activity. Explore the Fort Portal crater lake region in Kibale or high-altitude crater lakes in the Rwenzori foothills. Each lake offers unique ecosystems and breathtaking scenery.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      duration: '2-5 hours',
      difficulty: 'Moderate',
      price: 'From $40 per person',
      destination: 'Kibale/Fort Portal',
      category: 'Nature Exploration',
      link: '/activities/crater-lakes-drive'
    },
    {
      title: 'Sport Fishing',
      icon: 'fa-fish',
      tagline: 'Trophy Fish Adventures',
      description: 'Fish for massive Nile perch (up to 100kg) below Murchison Falls or target tilapia and catfish in various lakes. Professional guides provide equipment and know the best fishing spots. Both catch-and-release sport fishing and recreational fishing available.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
      duration: 'Half to full day',
      difficulty: 'Moderate',
      price: 'From $80 per person',
      destination: 'Murchison Falls NP',
      category: 'Nature Exploration'
    }
  ]

  const categories = [
    { name: 'All Activities', value: 'all' },
    { name: 'Primate Encounters', value: 'Primate Encounters' },
    { name: 'Mountain Adventures', value: 'Mountain Adventures' },
    { name: 'Water & River Sports', value: 'Water & River Sports' },
    { name: 'Wildlife Safaris', value: 'Wildlife Safaris' },
    { name: 'Cultural Experiences', value: 'Cultural Experiences' },
    { name: 'Extreme Adventures', value: 'Extreme Adventures' },
    { name: 'Nature Exploration', value: 'Nature Exploration' }
  ]

  const [selectedCategory, setSelectedCategory] = useState('all')

  // Fetch activities from CMS
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true)
        const response = await fetch('http://localhost:3001/api/activities?limit=100')

        if (!response.ok) {
          throw new Error('Failed to fetch activities')
        }

        const data = await response.json()

        // Transform CMS activities to match component format
        const transformedActivities = data.docs
          .filter(act => act.status === 'published')
          .map(act => ({
            title: act.title,
            icon: act.icon || 'fa-star',
            tagline: act.tagline || '',
            description: act.description || act.overview || '',
            image: act.featuredImage?.sizes?.card?.url || act.featuredImage?.url || 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800',
            duration: act.duration || 'Contact us',
            difficulty: act.difficulty || 'Moderate',
            price: act.price ? `From $${act.price} per person` : 'Contact for pricing',
            destination: act.destination?.name || 'Multiple Parks',
            category: act.category || 'Wildlife Safaris',
            link: `/activities/${act.slug}`,
            source: 'cms'
          }))

        // Combine CMS activities with hardcoded activities
        const combinedActivities = [
          ...hardcodedActivities.map(a => ({ ...a, source: 'hardcoded' })),
          ...transformedActivities
        ]

        setActivities(combinedActivities)
      } catch (err) {
        console.error('Error fetching activities:', err)
        // Fallback to hardcoded activities if CMS fails
        setActivities(hardcodedActivities)
      } finally {
        setLoading(false)
      }
    }

    fetchActivities()
  }, [])

  const filteredActivities = selectedCategory === 'all'
    ? activities
    : activities.filter(activity => activity.category === selectedCategory)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-safari-green"></div>
          <p className="text-gray-600 mt-4">Loading activities...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-safari-green to-safari-gold text-white py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600"
            alt="Adventure Activities"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 font-heading">
            Uganda Safari Adventure Activities
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">
            Experience Uganda's extraordinary biodiversity through safari adventure activities—from mountain gorilla encounters to Big Five game drives, boat safaris to cultural experiences. Each activity supports wildlife conservation and community partnerships
          </p>
        </div>
      </div>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Uganda offers exceptional safari adventure activities that showcase our country's remarkable wildlife, diverse ecosystems, and vibrant cultures. From tracking endangered mountain gorillas in ancient rainforests to witnessing the Big Five across vast savannah plains, each activity is designed to create meaningful connections with nature while supporting conservation efforts.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            As a Ugandan-owned safari company, our expert guides bring deep knowledge of wildlife behavior, ecosystems, and conservation challenges. Every safari activity contributes to habitat protection and community empowerment through our Planting Green Paths initiative—ensuring the natural wonders you experience remain protected for future generations.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${selectedCategory === category.value
                  ? 'bg-safari-green text-white shadow-lg transform scale-105'
                  : 'bg-white text-safari-green hover:bg-safari-green hover:text-white'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-20 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredActivities.map((activity, index) => (
              <div key={index} className="card group hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white rounded-full p-4 shadow-lg">
                    <FontAwesomeIcon icon={activity.icon} className="w-8 h-8 text-safari-green" />
                  </div>
                  <div className="absolute bottom-4 left-4 bg-safari-gold text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {activity.category}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-safari-green mb-2">
                    {activity.title}
                  </h3>
                  <p className="text-lg text-safari-gold italic mb-4">{activity.tagline}</p>

                  <p className="text-gray-700 mb-6 leading-relaxed line-clamp-3">
                    {activity.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-semibold">
                        <FontAwesomeIcon icon="fa-location-dot" className="w-4 h-4 mr-1" />
                        Destination:
                      </span>
                      <span className="text-gray-800">{activity.destination}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-semibold">
                        <FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-1" />
                        Duration:
                      </span>
                      <span className="text-gray-800">{activity.duration}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-semibold">
                        <FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-1" />
                        Difficulty:
                      </span>
                      <span className="text-gray-800">{activity.difficulty}</span>
                    </div>
                  </div>

                  {activity.price && (
                    <div className="mb-6 pb-6 border-t pt-4">
                      <p className="text-2xl font-bold text-safari-gold text-center">{activity.price}</p>
                    </div>
                  )}

                  {activity.link ? (
                    <Link
                      to={activity.link}
                      className="btn-primary w-full block text-center"
                    >
                      View Details
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleBooking(activity.title)}
                      className="btn-primary w-full"
                    >
                      Book This Activity
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Bring Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-safari-green">What to Bring</h2>

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-safari-light rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-camera" className="w-12 h-12 text-safari-green" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-safari-green">Camera</h3>
              <p className="text-gray-700 text-sm">Capture incredible wildlife and landscape moments</p>
            </div>

            <div className="text-center">
              <div className="bg-safari-light rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-sun" className="w-12 h-12 text-safari-green" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-safari-green">Sun Protection</h3>
              <p className="text-gray-700 text-sm">Hat, sunglasses, and high SPF sunscreen</p>
            </div>

            <div className="text-center">
              <div className="bg-safari-light rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-shirt" className="w-12 h-12 text-safari-green" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-safari-green">Appropriate Clothing</h3>
              <p className="text-gray-700 text-sm">Neutral colors, layers for varying temperatures</p>
            </div>

            <div className="text-center">
              <div className="bg-safari-light rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-binoculars" className="w-12 h-12 text-safari-green" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-safari-green">Binoculars</h3>
              <p className="text-gray-700 text-sm">Essential for wildlife spotting and bird watching</p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Guidelines */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-safari-green">Safety & Guidelines</h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="card text-center">
              <div className="bg-gradient-to-br from-safari-green to-safari-gold rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-safari-green">Expert Guides</h3>
              <p className="text-gray-700">All activities are led by experienced, certified guides who prioritize your safety</p>
            </div>

            <div className="card text-center">
              <div className="bg-gradient-to-br from-safari-gold to-safari-green rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-leaf" className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-safari-green">Respect Wildlife</h3>
              <p className="text-gray-700">Follow guidelines to minimize disturbance and protect animals in their natural habitat</p>
            </div>

            <div className="card text-center">
              <div className="bg-gradient-to-br from-safari-green to-safari-gold rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="fa-heart" className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-safari-green">Responsible Tourism</h3>
              <p className="text-gray-700">We support local communities and conservation efforts through ethical practices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-safari-green to-safari-gold rounded-2xl shadow-xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Adventure?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Combine multiple activities to create your perfect Uganda itinerary. Our team will help design a custom package that matches your interests, fitness level, and travel style.
            </p>
            <button onClick={() => handleBooking('Custom Activity Package')} className="bg-white text-safari-green px-8 py-4 rounded-full font-bold text-lg hover:bg-safari-light transition-colors duration-300">
              Plan Your Activities
            </button>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedActivity}
      />
    </div>
  )
}
